import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import MaterialTable from 'material-table';
import { ScaleLoader } from 'react-spinners';
import { ValidatorForm } from 'react-material-ui-form-validator';
import FileSaver from 'file-saver';
import { useSnackbar } from 'notistack';
import FileUploadRow from '../../../_shared/FileUploadRow';
import { getLocalization, MaterialTableIcons } from '../../../_shared/TableSettings';
import { ImportTemplateCodeEnum } from '../../../../helpers/Constants';
import { PartMappingImportRowDto } from '../../../../interfaces/Organization';
import { ICaseService } from '../../../../services/Interfaces/ICaseService';
import { IOrganizationService } from '../../../../services/Interfaces/IOrganizationService';
import { ITranslatorService } from '../../../../services/Interfaces/ITranslatorService';

interface IPartMappingImportProps {
  open: boolean;
  organizationId: number;
  translatorService: ITranslatorService;
  organizationService: IOrganizationService;
  caseService: ICaseService;
  onClose: () => void;
}

const PartMappingImport: React.FC<IPartMappingImportProps> = ({
  open,
  organizationId,
  translatorService,
  organizationService,
  caseService,
  onClose
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<PartMappingImportRowDto[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const translate = (key: string, fallback: string) => translatorService.Tranlate(key, fallback);

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const blob = await caseService.GetImportModel(
        ImportTemplateCodeEnum.PART_MAPPING_IMPORT_TEMPLATE
      );
      FileSaver.saveAs(
        blob,
        `${translate('PART_MAPPING_IMPORT_TEMPLATE_FILE_NAME', 'Mapare piese')}.xlsx`
      );
    } catch {
      enqueueSnackbar(translate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleImport = async () => {
    if (file === null) {
      enqueueSnackbar(
        translate('ERROR_MSG_FILE_NOT_SELECTED', 'Va rugam sa selectati un fisier!'),
        {
          variant: 'error'
        }
      );
      return;
    }

    setIsImporting(true);
    try {
      const result = await organizationService.ImportPartMappings({
        content: file,
        organizationId
      });
      setRows(result.rows);
      enqueueSnackbar(
        <div>
          <p>
            {translate('PART_MAPPING_IMPORT_PROCESSED', 'Date tratate: ')} {result.totalNumber}
          </p>
          <p>
            {translate('PART_MAPPING_IMPORT_SAVED', 'Tratate cu succes: ')} {result.importedNumber}
          </p>
        </div>,
        { variant: 'info', autoHideDuration: 6000 }
      );
    } catch {
      enqueueSnackbar(translate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    } finally {
      setIsImporting(false);
    }
  };

  const columns = [
    { field: 'line', title: translate('PART_MAPPING_IMPORT_LINE', 'Rând') },
    { field: 'bodyTypeName', title: translate('PART_MAPPING_BODY_TYPE_LABEL', 'Caroserie') },
    { field: 'partName', title: translate('PART_MAPPING_PART_LABEL', 'Piesă') },
    { field: 'damageTypeName', title: translate('PART_MAPPING_DAMAGE_TYPE_LABEL', 'Tip daună') },
    { field: 'operationCode', title: translate('PART_MAPPING_OPERATION_LABEL', 'Operație') },
    {
      field: 'damageSeverityCode',
      title: translate('PART_MAPPING_DAMAGE_SEVERITY_CODE_LABEL', 'Cod severitate')
    },
    { field: 'time', title: translate('PART_MAPPING_TIME_LABEL', 'Timp (ore)') },
    {
      field: 'importMessage',
      title: translate('PART_MAPPING_IMPORT_RESULT', 'Rezultat import'),
      render: (rowData: PartMappingImportRowDto) =>
        translate(rowData.importMessage, rowData.importMessage)
    }
  ];

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      fullScreen={true}
      fullWidth={true}
    >
      <DialogTitle
        id="customized-dialog-title"
        style={{ color: '#ffffff', backgroundColor: 'var(--primary)', textAlign: 'center' }}
      >
        <span style={{ fontSize: '20px', lineHeight: '24px' }}>
          {translate('PART_MAPPING_IMPORT_MODAL_TITLE', 'Importă mapare piese')}
        </span>
        <IconButton
          aria-label="close"
          className="position-absolute"
          style={{ right: 12, top: 12, color: '#ffffff' }}
          onClick={onClose}
          size={'small'}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box m={1} p={1}>
          <ValidatorForm onSubmit={handleImport} instantValidate={true}>
            <Paper
              variant="outlined"
              className="p-3 mb-3 d-flex flex-row align-items-center"
              style={{ borderRadius: 8 }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 6,
                  backgroundColor: '#FFF5F2',
                  marginRight: 16
                }}
              >
                <DescriptionOutlinedIcon style={{ color: '#FF7345' }} />
              </Box>
              <Box flexGrow={1}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {translate('PART_MAPPING_IMPORT_FORMAT', 'Format fișier acceptat: .XLSX')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {translate(
                    'PART_MAPPING_IMPORT_FORMAT_DESC',
                    'Descarcă modelul și păstrează structura lui înainte de import.'
                  )}
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleDownloadTemplate}
                disabled={isDownloading}
                startIcon={<GetAppOutlinedIcon />}
                style={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                {translate('PART_MAPPING_IMPORT_DOWNLOAD_TEMPLATE', 'Descarcă model')}
              </Button>
            </Paper>

            <FileUploadRow
              inputId="part-mapping-import-file"
              title={translate('PART_MAPPING_IMPORT_ADD_FILE', 'Adaugă fișier')}
              emptyDescription={translate(
                'PART_MAPPING_IMPORT_ADD_FILE_DESC',
                'Selectează un fișier .xlsx'
              )}
              uploadButtonLabel={translate('PART_MAPPING_IMPORT_UPLOAD_FILE', 'Încarcă fișier')}
              accept=".xlsx"
              fileName={file?.name ?? null}
              onFileSelected={(selected: File) => setFile(selected)}
              onFileRemoved={() => setFile(null)}
            />

            <div className="d-flex flex-row text-center flex-wrap justify-content-center">
              <ScaleLoader color={'var(--primary)'} loading={isImporting} />
            </div>

            {rows.length > 0 ? (
              <MaterialTable
                columns={columns}
                data={rows}
                title=""
                icons={MaterialTableIcons}
                localization={getLocalization(translatorService)}
                options={{ maxBodyHeight: 400, paging: false, sorting: false, search: false }}
                components={{ Container: (props) => <Paper {...props} elevation={0} /> }}
              />
            ) : null}

            <div className="text-right">
              <Button className="m-2" variant="outlined" color="default" onClick={onClose}>
                {translate('MAVO_VEHICLE_IMPORT_CLOSE_BTN', 'Renunta')}
              </Button>
              <Button
                className="m-2"
                variant="contained"
                color="secondary"
                type="submit"
                disabled={isImporting}
              >
                {translate('MAVO_VEHICLE_IMPORT_BTN', 'Importa')}
              </Button>
            </div>
          </ValidatorForm>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PartMappingImport;
